/**
 * Shared cartesian-chart JSX blocks (grid line, tooltip, legend) that were
 * byte-identical across BarChartCase/LineChartCase/AreaChartCase/
 * ComboChartCase/PieChartCase. See `shared.ts` for the pure (non-JSX)
 * helpers these build on.
 */
import { CartesianGrid, Legend, Tooltip } from 'recharts';
import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';

import {
  ChartLegendContent,
  ChartTooltipContent,
} from '@/lib/base/foundations/charts-base';

import type { LegendConfig } from '../types';
import { resolveCssColor } from './shared';

/**
 * Cartesian grid line — identical across Bar/Line/Area, with an added
 * `yAxisId` for the combo chart (which has two y-axes).
 */
export function ChartCartesianGrid({
  show,
  yAxisId,
}: {
  show: boolean | undefined;
  yAxisId?: string;
}) {
  if (!show) {
    return null;
  }
  return (
    <CartesianGrid
      yAxisId={yAxisId}
      strokeDasharray='3 3'
      stroke={resolveCssColor('--color-border-secondary')}
      vertical={false}
    />
  );
}

/**
 * Chart tooltip — identical across Line/Area/Combo; Bar adds a token-based
 * hover-band `cursor`, and Pie sets `isPieChart`.
 */
export function ChartTooltip({
  show,
  formatter,
  isPieChart,
  cursor,
}: {
  show: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Formatter type is complex, using any for callback
  formatter: (value: any, name: any, entry: any) => any;
  isPieChart?: boolean;
  cursor?: { fill: string };
}) {
  if (!show) {
    return null;
  }
  return (
    <Tooltip
      content={
        <ChartTooltipContent isPieChart={isPieChart} formatter={formatter} />
      }
      cursor={cursor}
    />
  );
}

/**
 * Cartesian legend — identical across Bar/Line/Area (no explicit payload,
 * recharts derives one from the rendered series) and Combo (explicit
 * `payload` naming both the bar and line series).
 */
export function CartesianLegend({
  show,
  legend,
  legendAlign,
  payload,
}: {
  show: boolean | undefined;
  legend: LegendConfig;
  legendAlign: 'left' | 'center' | 'right';
  payload?: LegendPayload[];
}) {
  if (!show) {
    return null;
  }
  return (
    <Legend
      verticalAlign={
        legend.position === 'top' || legend.position === 'bottom'
          ? legend.position
          : 'top'
      }
      align={legendAlign}
      layout={legend.layout}
      content={
        <ChartLegendContent
          payload={payload}
          align={legendAlign}
          layout={legend.layout}
        />
      }
      wrapperStyle={{ paddingTop: 16 }}
    />
  );
}

/**
 * Legend content for pie charts: renders the per-slice entries supplied via
 * `payloadOverride`, ignoring the payload recharts derives. Recharts clones
 * element content with its own computed props (which win a cloneElement
 * merge), so the slice entries ride an override prop recharts never sets.
 */
export function PieLegendContent({
  payloadOverride,
  ...contentProps
}: Parameters<typeof ChartLegendContent>[0] & {
  payloadOverride: LegendPayload[];
}) {
  return <ChartLegendContent {...contentProps} payload={payloadOverride} />;
}
